/**
 * useSnowflakeKeypair — fetches (or creates) the workspace RSA key pair for the
 * managed-key flow. The form derives the setup SQL from the returned key body;
 * SQL generation itself lives in the form (a pure, reactive computation), so this
 * hook only owns the network round-trip.
 *
 * Modeled as a query (not a fire-on-mount mutation) so it is StrictMode-safe:
 * React Query dedupes the dev double-mount into a single cached request, and the
 * live observer reads the cached result. A mutation's per-call `onSuccess` is
 * bound to the mount-time observer and is silently dropped when StrictMode
 * unmounts it — which left the form stuck on "Generating an RSA key pair…" even
 * though the request succeeded. getOrCreateKeypair is idempotent (get-or-create),
 * so running it as a cached query is safe.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { toast } from 'sonner';

import { useOrpc } from '@/_shared/hooks/useOrpc';

import { isPermissionRefusal, refreshPermissionOnRefusal } from '../accountCreatePermission';
import { refusalMessage } from '../refusalCopy';

export interface KeypairInfo {
  publicKeyPem: string;
  publicKeyBody: string;
  fingerprint: string;
}

export interface SnowflakeKeypair {
  keypairInfo: KeypairInfo | null;
  isFetching: boolean;
  /** True when the last fetch attempt failed and no key pair is available. */
  failed: boolean;
  /** Re-attempt the fetch (used by the form's Retry affordance). */
  retry: () => void;
}

/** `enabled: false` skips the fetch, which on first use also creates the workspace key pair. */
export function useSnowflakeKeypair(enabled = true): SnowflakeKeypair {
  const orpc = useOrpc();
  const queryClient = useQueryClient();

  const query = useQuery({
    ...orpc.settings.warehouses.getOrCreateKeypair.queryOptions({ input: {} }),
    // The key pair is stable for the workspace — fetch once, never auto-refetch.
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
    enabled,
  });

  // Surface one toast per failed attempt. errorUpdateCount increments on each new
  // error, so a retry that fails toasts again; the ref guard keeps StrictMode's
  // double effect-run (and re-renders) from duplicating a single failure's toast.
  const toastedErrorCount = React.useRef(0);
  React.useEffect(() => {
    if (query.isError && query.errorUpdateCount > toastedErrorCount.current) {
      toastedErrorCount.current = query.errorUpdateCount;
      // A RudderStack refusal is not a key failure: say why and reread the permission, which
      // brings up the page's sign-in or retry notice.
      refreshPermissionOnRefusal(queryClient, orpc, query.error);
      toast.error(
        isPermissionRefusal(query.error) || isNotLinked(query.error)
          ? refusalMessage(query.error, 'Failed to generate key pair')
          : 'Failed to generate key pair',
      );
    }
  }, [query.isError, query.errorUpdateCount, query.error, queryClient, orpc]);

  const keypairInfo = React.useMemo<KeypairInfo | null>(
    () =>
      query.data
        ? {
            publicKeyPem: query.data.publicKeyPem,
            publicKeyBody: query.data.publicKeyBody,
            fingerprint: query.data.fingerprint,
          }
        : null,
    [query.data],
  );

  return {
    keypairInfo,
    isFetching: query.isFetching,
    failed: query.isError,
    retry: () => void query.refetch(),
  };
}

/** The gate's "Link a RudderStack workspace first." refusal. */
function isNotLinked(err: unknown): boolean {
  return (err as { data?: { reason?: unknown } } | null)?.data?.reason === 'workspace_not_linked';
}
